import React from 'react';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import HourglassEmpty from '@material-ui/icons/HourglassEmpty';
import TextInput from '../TextInput';
import DefaultButton from '../Buttons/DefaultButton/button';
import {
   MobileNumberDetails,
   MobileNumberVerifyDetails,
   MobileNumberValidationDetails,
   VERIFICATION_CODE_LENGTH,
   getFieldClassName,
} from './types';

export const VerifyingStatusChip = () => (
   <Chip
      className="verification-status-chip verification-status-chip-verifying"
      icon={<HourglassEmpty className="verification-status-chip-icon" />}
      label="Verifying"
   />
);

interface MobileNumberVerifyProps {
   mobileNumberDetails: MobileNumberDetails;
   verifyDetails?: MobileNumberVerifyDetails;
   resendOtpData?: MobileNumberValidationDetails;
   resendCooldown?: number;
   isVerifyingAndSaving?: boolean;
   isResendingOtp?: boolean;
   isCancelling?: boolean;
   verificationCode?: string;
   inputValid?: boolean;
   onVerificationCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onVerify: () => void;
   onResend: () => void;
   onCancel: () => void;
}

const MobileNumberVerify = ({
   mobileNumberDetails,
   verifyDetails,
   resendOtpData,
   resendCooldown = 0,
   isVerifyingAndSaving = false,
   isResendingOtp = false,
   isCancelling = false,
   verificationCode = '',
   inputValid,
   onVerificationCodeChange,
   onVerify,
   onResend,
   onCancel,
}: MobileNumberVerifyProps) => {
   const error =
      verifyDetails && !verifyDetails.isSuccess
         ? verifyDetails.errorMessage
         : null;

   const resendError =
      resendOtpData && !resendOtpData.isSuccess
         ? resendOtpData.errorMessage
         : null;

   const isCoolingDown = resendCooldown > 0;
   const isBusy = isVerifyingAndSaving || isResendingOtp || isCancelling;

   return (
      <div className={getFieldClassName(inputValid)}>
         <div className="mobile-number-text-field-header">
            <span className="grey2-text input-header">
               Contact Mobile Number <span className="text-red">*</span>
            </span>
            <VerifyingStatusChip />
         </div>
         <div className="mobile-number-text-field-input">
            <TextInput
               type="tel"
               id="mobile_number_verification_code"
               placeholder="Verification code"
               value={verificationCode || ''}
               maxLength={VERIFICATION_CODE_LENGTH}
               inputValid={!error}
               onChange={onVerificationCodeChange}
               onSubmit={onVerify}
               disabled={isBusy}
               mandatory
            />
            {isVerifyingAndSaving && (
               <CircularProgress
                  className="mobile-number-text-field-spinner"
                  size={18}
                  thickness={4}
               />
            )}
         </div>

         {error && (
            <span className="text-red mobile-number-error">{error}</span>
         )}

         {resendError && (
            <span className="text-red mobile-number-error">{resendError}</span>
         )}

         <span className="mobile-number-hint">
            Enter the {VERIFICATION_CODE_LENGTH}-digit code sent to{' '}
            {mobileNumberDetails.mobileNumber}.
         </span>

         <div className="mobile-number-text-field-actions">
            <DefaultButton
               className="btn"
               type="primary"
               text="Verify"
               onClick={onVerify}
               disabled={
                  isBusy || verificationCode.length !== VERIFICATION_CODE_LENGTH
               }
            />
            <DefaultButton
               className="btn"
               type="tertiary"
               text={
                  isCoolingDown
                     ? `Resend Code (${resendCooldown}s)`
                     : 'Resend Code'
               }
               onClick={onResend}
               disabled={isBusy || isCoolingDown}
            />
            <DefaultButton
               className="btn"
               type="secondary"
               text="Cancel"
               onClick={onCancel}
               disabled={isCancelling}
            />
         </div>
      </div>
   );
};

export default MobileNumberVerify;
